import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../../voyage-svg-icons/src/voyage-svg-icons';
import { VesselPlanningModel, VoyagePlan, CapturedPlanNotifications } from '../../../../src/models/vessel-planning-model';
import type { BprRejectNotificationData, RouteSelectionNotificationData } from '../../../../src/services/vessel-notifications-service';
import { DataExtractor } from '../data-extractor';
import { nothing } from 'lit-html';
import '../../../voyage-spinner/src/voyage-spinner';
import { _checkRouteAlignmentToStarRoutes, createNotificationAdoptingStarRoute, createNotificationForChoosingOwnRoute, getStarRouteDeviationSegments, isStarRoute, verifyRequestedETAs } from '../../../../src/utils/route-utils';
import { VoyageBestPracticeRoutesModel } from '../../../../src/models/voyage-best-practice-routes-model'
import { VoyageWeatherRoutingModel } from '../../../../src/models/weather-routing-model';
import { getStarRoutesInPlan, VesselNotificationsService } from '../../../../src/services/vessel-notifications-service';
import "@maersk-global/mds-components-core/mc-toast";
import { voyagePassagePlanOverviewStyles } from './voyage-passage-plan-overview.styles'
import { BestPracticeRoutePayload } from '../../../../src/services/types/vessel-notifications-services.types'
import { VesselAlarmsModel } from '../../../../src/models/vessel-alarms-model'
import { VoyageDialogPopupManager } from '../../../voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { AlignBPREvent, NotificationMessageType } from './type';
import { AlertType, VesselAlertsModel } from '../../../../src/models/vessel-alerts-model';
import { getFormattedDateUTC, isOnline, isStarRouteDeviationEnabled, isWithinBprReasonWindow } from '../../../../src/utils/helpers';
import { captureStarRouteWrWorkflowEvent, faro } from '../../../../src/utils/rum-utility';
import { ScheduleEntry } from '../../../../src/models/types/voyage-schedules-model.types';
import { IndexedDBService } from '../../../../indexed-db/services/db_service';
import { VoyageImoModel } from '../../../../src/models/voyage-imo-model';
import { AuthService } from '../../../../src/services/auth/auth-service';
import { BprAdoption, BprPopupSkipReason, VoyageRoute } from '../../../../src/models/voyage-routing-model';
import { VesselPlansService } from '../../../../src/services/vessel-plans-service';
import type { BprAPIResponse } from '../../../../src/models/types/voyage-best-practice-routes-impl.types';
import { VesselNotificationsModel } from '../../../../src/models/vessel-notifications-model';
import { VesselRouteSelectionModel } from '../../../../src/models/vessel-route-selection-model';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
@customElement('voyage-passage-plan-overview')
export class VoyagePassagePlanOverview extends LitElement {
    @property({ type: Boolean }) showPassagePlanFromPlan: boolean = false;
    @property({ type: Object, attribute: false }) plan: VoyagePlan | null = null
    @property({ type: Boolean }) isOfflinePlan: boolean = false
    @property({ type: Boolean }) private _showConfirmation: boolean = false
    @property({ type: Boolean }) private _approving: boolean = false
    @property({ type: Function }) toggleFuelEfficientModal: (isVisible: boolean) => void = () => {};
    @property({ type: Function }) closePassagePlaneModal: () => void = () => {};
    @property({attribute: false}) private _currentSchedule: ScheduleEntry | null = null;
    @property({ type: Boolean }) private _planUpdateFailure: boolean = false;
    private _isAligned = false;
    private _isFuelEfficient = false;
    private _popupSkipReason: BprPopupSkipReason | undefined = undefined;
    // The Star Routes the port-pair endpoint offered when the approve button was clicked.
    private _offeredStarRoutes: BprAPIResponse[] | null = null;

    
    static get styles() {
        return [voyagePassagePlanOverviewStyles];
    }

    constructor() {
        super()
        this.approveAndSend = this.approveAndSend.bind(this)
        this._onCancelPlanClf = this._onCancelPlanClf.bind(this)
        this.savePlan = this.savePlan.bind(this)
        this.checkPlanUpdateFailure = this.checkPlanUpdateFailure.bind(this)
        this.isMoreFuelEfficientThanStarRoute = this.isMoreFuelEfficientThanStarRoute.bind(this)
    }

    connectedCallback(): void {
        super.connectedCallback()
        VesselPlanningModel!.addEventListener('approve-plan-from-clf', this.approveAndSend)
        VesselPlanningModel!.addEventListener('cancel-plan-from-clf', this._onCancelPlanClf)
        VoyageImoModel!.addEventListener('updating-plan-event-failed', this.checkPlanUpdateFailure);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback()
        VesselPlanningModel!.removeEventListener('approve-plan-from-clf', this.approveAndSend)
        VesselPlanningModel!.removeEventListener('cancel-plan-from-clf', this._onCancelPlanClf)
        VoyageImoModel!.removeEventListener('updating-plan-event-failed', this.checkPlanUpdateFailure);
    }

    firstUpdated() {
        this.getCurrentSchedule();
    }

    private async getCurrentSchedule() {
        const currentSchedule = await IndexedDBService.schedules.get(this.plan!!.scheduleId);
        if (currentSchedule) {
            this._currentSchedule = currentSchedule;          
        }
    }

    private _handleBgSync = async () => {
        // if application is offline and plan approval request is initiated and the api call is queued for background sync.
                if (this._approving && this.plan!.status === 'approved') {
                this._approving = false
                this.plan && this.checkIfOwnRouteIsMoreFuelEfficient(this.plan)
                this.requestUpdate(); // Ensure the component re-renders
            }
    }

    private _onCancelPlanClf() {
        this._showConfirmation = false
        this.requestUpdate()
    }

    async _renderExceedLimitPopup(currentSelectedRouteIndex: number, starRoutes?: BprAPIResponse[] | null) {
         let deviationDetail = '';
         if (isStarRouteDeviationEnabled()) {
             const selectedRoute = this.plan?.routes?.[currentSelectedRouteIndex] ?? this.plan?.routes?.[this.plan?.selectedRouteIndex ?? -1];
             const segments = selectedRoute?.route_geometry ? getStarRouteDeviationSegments(selectedRoute.route_geometry) : [];
             if (segments.length) {
                 deviationDetail = `\n\nSegments outside the deviation limits: ${segments.join(', ')}.`;
             }
         }
         const dialog = VoyageDialogPopupManager.createDialog({
            headerText: "Optimize Your Voyage With StarRoute",
            bodyText: `The current modifications exceed the StarRoute's recommended limits(including maximum crosstrack deviation of 10 NM from Rhumb line and/or 50 NM from the Great circle). Following StarRoute's guidance is essential to maintain fuel efficiency and achieve optimal voyage conditions.${deviationDetail} \n\nPlease select one of the options to proceed.`,
            acceptButtonText: "Switch to Star Route",
            secondaryButtonText: "Compare and adjust route",
            rejectButtonText: "Continue with own route",
            alignLeft: true
        });

        const response = await dialog.getResponse();
        if (response === "accept") {
            if(!this.plan) return
            // select star route 1 as selected route and close the passage plan - navigate back to plannig view
            const starRouteOne = this.plan?.routes?.filter(route => route.routeCreatedBy === 'system' && route.name === 'Star Route 1'); 
            const selectedRouteIndex = this.plan?.routes.findIndex((route) => route.routeId === starRouteOne[0]?.routeId);  
            VesselPlanningModel?.dispatchEvent(new CustomEvent(AlignBPREvent.ClosePassagePlan));
            VesselPlanningModel?.dispatchEvent(new CustomEvent(AlignBPREvent.SelectStarRouteOne,  {detail: { routeId: starRouteOne[0]?.routeId, index: selectedRouteIndex }}));
            this._showConfirmation = false
            this.requestUpdate()    

        } 
        else if(response === "reject") {
            if(!this.plan) return
            verifyRequestedETAs(
                this.plan.routes[this.plan!.selectedRouteIndex],
                this.plan,
                this.plan.scheduleId,
                currentSelectedRouteIndex
            )
            if (this.isWithinReasonWindow()) {
                try {
                    await createNotificationForChoosingOwnRoute(this.plan, this.plan.scheduleId, starRoutes)
                } catch (err) {
                    // The reject-reason popup could not be opened; approval must
                    // still be possible via the confirmation below.
                    console.error('Failed to open the own-route reason popup', err)
                }
            }
            this._showConfirmation = true
        }
        else {
            // ope modal with available star routes

            VesselPlanningModel?.dispatchEvent(new CustomEvent(AlignBPREvent.ClosePassagePlan));
            VesselPlanningModel?.dispatchEvent(new CustomEvent(AlignBPREvent.ShowStarRouteModal));

            this._showConfirmation = false
            this.requestUpdate()    
        }
    }



    render() {
        if (!this.plan) {
            return nothing
        }
        const curentApprovedRouteId = this.plan.routes[this.plan.selectedRouteIndex]?.routeId ?? "";
        const buttonsClass = (isVesselOnCloud && !isOnline()) && 
        (this.plan.status === 'approved' && curentApprovedRouteId == this.plan.currentSelectedRouteId)||(this.plan.status === 'approved' && this.showPassagePlanFromPlan) 
        ? 'buttons app-offline' : 'buttons';
        const lastSyncedTime = localStorage.getItem('lastSyncedTime');
        const currentSelectedRoute = this.plan.routes.find(route => route.routeId === this.plan?.currentSelectedRouteId);
        const averageSpeed = this.plan.passagePlan.calculatedValues.avgSpeedOGPilotToPilot;
        const showAverageSpeed = (!averageSpeed || (isVesselOnCloud && !isOnline() && currentSelectedRoute?.recalcNeeded)) ? false : true; 
        const hasBasicPlan = this._currentSchedule ? (this._currentSchedule.isBasicPlanAvailable || false ) : false;
        const showOfflineMessage = isVesselOnCloud && !isOnline() && !hasBasicPlan &&
            (
                !(this.plan.status === 'approved' && curentApprovedRouteId == this.plan.currentSelectedRouteId) ||
                (this.plan.status === 'approved' && this.showPassagePlanFromPlan)
            );

        return html`
            <div class="content" data-test="overview-content">
                <h2>INFORMATION</h2>

                <h3>Description</h3>
                <p>
                    ${DataExtractor.getDataFromPlan('description', this.plan, this.showPassagePlanFromPlan)}
                </p>

                <div class="ports">
                    <div class="port">
                        <h3>Departure Port</h3>
                        <p>
                            ${DataExtractor.getDataFromPlan(
                                'departurePort',
                                this.plan,
                                this.showPassagePlanFromPlan
                            )}
                        </p>
                    </div>
                    <div class="port">
                        <h3>Arrival Port</h3>
                        <p>
                            ${DataExtractor.getDataFromPlan(
                                'arrivalPort',
                                this.plan,
                                this.showPassagePlanFromPlan
                            )}
                        </p>
                    </div>
                </div>

                <h3>Port Memo</h3>
                <p>${DataExtractor.getDataFromPlan('portMemo', this.plan,  this.showPassagePlanFromPlan)}</p>

                <h3>Average Speed Over Ground (Pilot/Pilot)</h3>
                <p>
                    ${showAverageSpeed
                        ? averageSpeed?.toFixed(
                              1,
                          ) + 'kt'
                        : ''}
                </p>

                <h3>Reminders</h3>
                <p class="pre-wrap">
                    ${DataExtractor.getDataFromPlan('reminders', this.plan, this.showPassagePlanFromPlan)}
                </p>

                <h2>CHARTS & LIGHTS</h2>

                <h3>ENC Cells Used</h3>
                <p class="pre-wrap">
                    ${DataExtractor.getDataFromPlan('encCellsUsed', this.plan, this.showPassagePlanFromPlan)}
                </p>

                <h2>
                    THE FOLLOWING SHOULD BE MARKED ON THE CHART, WHERE IT
                    ENHANCES SAFE NAVIGATION:
                </h2>
                <p>
                    Parallel indexing (not from floating objects unless they
                    have been first checked for position)<br />
                    Methods and frequency of position fixing<br />
                    Last position from previous chart carried forward to next
                    chart<br />
                    Prominent navigation and radar marks<br />
                    No-go areas (the excessive marking of no-go areas should be
                    discouraged - see below)<br />
                    Landfall targets and lights<br />
                    Clearing lines and bearings<br />
                    Transits, heading marks and leading lines<br />
                    Significant tides or current<br />
                    Safe speed and necessary speed alterations<br />
                    Changes in machinery status<br />
                    Minimum under keel clearance<br />
                    Positions where the echo sounder should be activated<br />
                    Crossing and high density traffic areas<br />
                    Safe distance off<br />
                    Anchor clearance<br />
                    Contingency plans, emergency anchoring positions<br />
                    Abort positions<br />
                    VTS and reporting points, etc.<br />
                </p>
                <p>
                    Passage planning information displayed on ECDIS shall be clear, relevant
                    and not excessive, ensuring that critical charted information remains visible 
                    and unobscured. No-go areas shall be clearly defined using ECDIS safety settings 
                    and user added information, but only where necessary to draw attention to genuine 
                    hazards, such as shallow waters, wrecks or obstructions close to the intended track. 
                    Excessive or indiscriminate marking of no-go areas should be avoided. Safety settings 
                    and route checks shall be reviewed whenever there is a significant change in the 
                    vessel's condition or the passage plan and any updates shall be reflected in the ongoing 
                    voyage. Where paper charts are used as a backup arrangement, they shall be corrected 
                    and maintained adequately for emergency use. However, ECDIS shall remain the primary 
                    reference for navigation and voyage monitoring.
                </p>
            </div>
            ${
                showOfflineMessage ? html`
                <div class="infoContanier offlinePlan">
                    <div class="icon">
                        <span class="network-status-icon"> ${SvgIcons.customCloudOffline}</span>
                    </div>
                    <div class="text">
                        <div class ="offlineText">
                            Please note: You are currently offline. You can still 'Approve & Send and the route will be calculated,
                            send automatically when connection is restored.                  
                        </div>
                        <div class="syncText">
                            Last synced: ${getFormattedDateUTC(lastSyncedTime ? new Date(lastSyncedTime) : new Date())}
                        </div>
                    </div>
                    
                </div>
                `: html``
            }
            <div class=${buttonsClass} ?approved=${this.plan.status === 'approved'}>
                ${this._showConfirmation
                    ? html`
                          <div>
                              <div class="approveText">
                                  Are you sure you want to approve this Passage
                                  Plan and send it to Voyage Execution?
                              </div>
                              <button
                                  @click=${() =>
                                      (this._showConfirmation = false)}
                              >
                                  No, Cancel
                              </button>
                              <button
                                  id="confirmBtn"
                                  class="blueButton"
                                  @click=${this.approveAndSend}
                              >
                                  Yes
                              </button>
                          </div>
                      `
                    : html`
                          ${this._approving
                              ? html`
                                    <div class="approving">
                                        <voyage-spinner></voyage-spinner>
                                        <div>APPROVING...</div>
                                    </div>
                                `
                              : (this._planUpdateFailure ? html`
                                    <div class="errorText">
                                        ${SvgIcons.exclamationTriangle}Current Plan changes failed to be saved. Please try again. If issue persist, please create a support incident.
                                    </div>
                                
                                `: html`
                                    ${
                                        (this.plan.status === 'approved' && curentApprovedRouteId == this.plan.currentSelectedRouteId)||(this.plan.status === 'approved' && this.showPassagePlanFromPlan)    
                                        ? html`
                                            ${(isVesselOnCloud && !isOnline()) ? html`
                                                <div class="approvedText">
                                                    ${SvgIcons.solidBadgeCheck}Passage Plan
                                                    approved now route will be calculated and sent automatically when connection is restored
                                                    when connection is restored.
                                                </div>
                                                <div class="logLinkText">
                                                You can track its status by looking into the Activity log section
                                                </div>
                                            `: html`
                                                <div class="approvedText">
                                                    ${SvgIcons.solidBadgeCheck}Passage Plan
                                                    approved and sent
                                                </div>
                                            `}
                `
                                        : this._showApproveButton
                                        ? html`
                                            <button
                                                id="approveButton"
                                                class="largeButton blueButton"
                                                ?disabled=${(isVesselOnCloud && hasBasicPlan) || this.isOfflinePlan }
                                                @click=${this.approveBtnClicked}
                                            >
                                                ${SvgIcons.solidBadgeCheck}Approve &
                                                Send
                                            </button>
                                            <div
                                                id="approve-btn-not-allowed-text"
                                                ?show=${this.isOfflinePlan}
                                            >
                                                Route calculation failed and you can not
                                                Approve and Send Plan.<br />

                                                It is possible to Print Passage Plan, to
                                                make sure you have plan for future use
                                                make sure to download it using the
                                                "Download selected route to..."<br />

                                                Passage plan can be approved and sent
                                                when calculation issue have been
                                                resolved.
                                            </div>
                                        `
                                        : html`
                                            <button
                                            id="saveButton"
                                            class="largeButton blueButton"
                                            ?disabled=${this.isOfflinePlan}
                                            @click=${this.savePlan}
                                            >
                                                Save
                                            </button>
                                        `
                                    }
                                `)}
                      `}
            </div>
        `
    }

    @property({ type: Boolean }) private _showApproveButton: boolean = true
    
    updated(changedProperties: PropertyValues) {
        changedProperties.forEach((oldValue, propName) => {
            if (propName === 'plan') {
                if (this._approving && this.plan!.status === 'approved') {
                    this._approving = false
                    this.plan && this.checkIfOwnRouteIsMoreFuelEfficient(this.plan)
                    this.requestUpdate(); // Ensure the component re-renders
                }
                this.showApproveButton();
            }
        });
    }

    showApproveButton(){
        const etd = this.plan?.etd;
        const timeNow = Date.now();
        // avoid show approve and send button, in case it's more than 60 hrs before etd
        if(etd && etd - timeNow > 60 * 60 * 1000 * 60){
            this._showApproveButton = false;
        } else {
            this._showApproveButton = true;
        }
    }
    savePlan(){
        if (!this.plan) {
            return
        }
        this.dispatchEvent(new CustomEvent('close', {
            bubbles: true,
            composed: true
        }));

        VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, {
            title: "Thank you for planning in advance",
            message: NotificationMessageType.NoPlanMoreThan60Message,
            id: this.plan.scheduleId,
            type: AlertType.NoPlanEtd,
        });
    }

    // gets current selected route index and route based on the plan's currentSelectedRouteId
    private getRoute(): { currentSelectedRouteIndex: number, selectedRoute: VoyageRoute } {
        const currentSelectedRouteIndex = this.plan!.routes.findIndex((route) => route.routeId === this.plan!.currentSelectedRouteId);
        const selectedRoute = this.plan!.routes[currentSelectedRouteIndex];
        return {
            currentSelectedRouteIndex,
            selectedRoute
        };
    }

    // The reason for not choosing a Star Route, and the Star Route adoption verdict, are collected until one
    // day after ETD (never after ETA): vessels often plan the route, seeing the Star Routes, before departure
    // but approve and send the plan shortly after departure.
    private isWithinReasonWindow(): boolean {
        const etd = this.plan?.etd;
        const eta = this.plan?.eta;
        // log warning if etd/eta is string so it can be visible in grafana, as they are expected to be numbers
        if (typeof etd === 'string' || typeof eta === 'string') {
            console.warn('ETD or ETA is a string, expected to be a number. Attempting to parse it to a timestamp, for schedule_id and imo.', {
                etd: etd, eta: eta, scheduleId: this.plan?.scheduleId, imo: this.plan?.imoNo
            });
        }
        return isWithinBprReasonWindow(etd, eta);
    }

    // hasStarRouteForReason reports whether a Star Route was available to the user. Before departure the Star
    // Routes offered by the port-pair endpoint count; after departure only a Star Route in the plan, i.e. one
    // the user saw while planning.
    private hasStarRouteForReason(offeredStarRoutes?: BprAPIResponse[] | null): boolean {
        if (this.plan?.routes.some(isStarRoute)) {
            return true;
        }
        const etd = this.plan?.etd;
        const etdTimestamp = typeof etd === 'string' ? new Date(etd).getTime() : etd;
        return !!offeredStarRoutes?.length && !!etdTimestamp && etdTimestamp > Date.now();
    }

    // approval button click handler, checks for route alignment and fuel efficiency, then shows popup if route is not aligned to star route and not fuel efficient
    /**
     * ==================================================================================
     * SCENARIO: BEFORE ARRIVAL - Approve Button Clicked
     * ==================================================================================
     * 
     * This method executes when user clicks "Approve" button, before or after departure.
     * 
     * PURPOSE: Determine if route justifies not using Star Route recommendations
     * 
     * WHAT HAPPENS:
     * 
     * 1. RETRIEVE ALIGNMENT STATUS
     *    • Reads selectedRoute.bprAlignment.isAligned
     *    • This was set during "Save & Exit" in route editor (_saveAndClose)
     *    • If missing or false, re-checks via API
     * 
     * 2. CALCULATE FUEL EFFICIENCY
     *    • Calls isMoreFuelEfficientThanStarRoute()
     *    • Compares own route fuel vs star routes
     *    • Returns true ONLY if own route uses less fuel than EVERY star route in plan
     * 
     * 3. DECISION LOGIC - "Exceed Limit Popup" (UNTIL ETD + 1 DAY, NEVER AFTER ETA)
     *    Shows popup when ALL these are true:
     *    ✗ A Star Route was available (after departure: only a Star Route in the plan)
     *    ✗ Route is NOT aligned (isAligned = false)
     *    ✗ Route is NOT more fuel efficient (isFuelEfficient = false)
     *    ✗ now < ETD + 1 day AND now < ETA
     * 
     *    Does NOT show popup if EITHER:
     *    ✓ isAligned = true (route follows a star route path)
     *    ✓ isFuelEfficient = true (route saves fuel vs all star routes in plan)
     * 
     * WHAT DETERMINES isAligned = true:
     * • Route geometry matches Star Route within acceptable deviation limits
     * • Set during Save & Exit → _setRouteAlignment() → backend API /bpr/adopted
     * 
     * WHAT DETERMINES isFuelEfficient = true:
     * • Own route's non-ECA fuel < ALL star routes' fuel consumption
     * • Calculated by comparing route_info.totals.non_eca.fuel values
     * 
     * FLOW CONTINUES TO: approveAndSend() when user confirms approval
     */
    async approveBtnClicked() {
        if (!this.plan) {
            // sainity check, plan should always be available when approve button is clicked, as button is not rendered if plan is missing
            console.error('Plan data is missing when approve button is clicked');
            return
        }
        const { currentSelectedRouteIndex, selectedRoute } = this.getRoute();
        const starRoutes = await VesselPlansService.getOptimizedBPRsByPortPair(this.plan.departureTerminalCode, this.plan.arrivalTerminalCode, this.plan.scheduleId);
        this._offeredStarRoutes = starRoutes;

        // Check if route is already aligned from "save & exit" procedure in route editor.
        // This alignment was determined in voyage-route-editor._saveAndClose() -> _setRouteAlignment()
        // which calls the backend API to verify geometric alignment with star routes.
        let isAlignedToStarRoute = selectedRoute.bprAlignment?.isAligned;

        // not expected to ever happen, but in case alignment data is missing, log an error so it can be visible in grafana
        if (isAlignedToStarRoute === undefined) {
            // log the full bprAlignment data for debugging, in scenario when isAligned is missing
            console.error('BPR alignment data is missing for the selected route and plan', {
                routeId: selectedRoute?.routeId,
                bprAlignment: selectedRoute?.bprAlignment,
                planId: this.plan.id,
                scheduleId: this.plan.scheduleId,
                imo: this.plan.imoNo
            });
        }

        // if route alignment is false or missing, then re-check route alignment towards star routes.
        // property isAlignedToStarRoute will be missing in the scenario StarRoute calculation not completed upon Save & Exit.
        if (!isAlignedToStarRoute) {
            isAlignedToStarRoute = await _checkRouteAlignmentToStarRoutes(selectedRoute, this.plan.departureTerminalCode, this.plan.arrivalTerminalCode, this.plan.scheduleId);
        }

        // Check if own route is more fuel efficient than ALL star routes.
        // Returns true only if own route fuel < every star route's fuel consumption.
        this._isFuelEfficient = this.isMoreFuelEfficientThanStarRoute(this.plan) || false;

        // Show "exceed limit" popup if:
        // - A Star Route was available to the user (before departure: offered by the port-pair endpoint or
        //   held by the plan; after departure: held by the plan) AND
        // - Route is not aligned AND
        // - Route is not more fuel efficient AND
        // - It is at most one day after ETD and before ETA (vessels often plan before departure but
        //   approve and send the plan shortly after departure)
        this._isAligned = isAlignedToStarRoute || false;
        const hasStarRoute = this.hasStarRouteForReason(starRoutes);
        if (hasStarRoute && !isAlignedToStarRoute && !this._isFuelEfficient && this.isWithinReasonWindow()) {
            this._popupSkipReason = undefined; // popup is being shown
            // The dialog's branches control _showConfirmation, so the plan cannot
            // be approved & sent while the star-route choice is still open.
            this._renderExceedLimitPopup(currentSelectedRouteIndex, starRoutes);
        } else {
            if (!hasStarRoute) {
                this._popupSkipReason = 'noBpr';
            } else if (isAlignedToStarRoute) {
                this._popupSkipReason = 'aligned';
            } else if (this._isFuelEfficient) {
                this._popupSkipReason = 'fuelEfficient';
            } else {
                this._popupSkipReason = 'postDeparture';
            }
            this._showConfirmation = true
        }

        // if plan has infeasible ETAs, warn the user
        const routeToVerify = this.plan.routes[this.plan.selectedRouteIndex];
        if (routeToVerify) {
            verifyRequestedETAs(
                routeToVerify,
                this.plan,
                this.plan.scheduleId,
                currentSelectedRouteIndex
            )
        }
    }

    /**
     * ==================================================================================
     * SCENARIO: BEFORE ARRIVAL - Approve and Send Flow
     * ==================================================================================
     * 
     * This method executes when user confirms plan approval, before or after departure.
     * 
     * WHAT HAPPENS:
     * 
     * 1. APPROVE PLAN
     *    • Calls VesselPlanningModel.approvePlan()
     *    • Marks plan as approved in system
     * 
     * 2. SEND BEST PRACTICE ROUTE NOTIFICATION (if applicable)
     *    • Standard BPR notification with reason/comment
     * 
     * 3. SEND STAR ROUTE ADOPTION NOTIFICATION (UNTIL ETD + 1 DAY, NEVER AFTER ETA)
     *    Sent when: (isAligned OR isFuelEfficient) AND now < ETD + 1 day AND now < ETA AND a Star Route was
     *    available (after departure: only a Star Route in the plan) AND NO WR ADVISE
     *    
     *    CRITICAL: WR ADVISE CHECK
     *    • Checks if Weather Routing advise exists for this voyage schedule
     *    • If WR advise exists, NO star route adoption notification is sent
     *    • This prevents duplicate/conflicting route justification notifications
     *    
     *    NOTIFICATION REASON (Priority Order):
     *    
     *    Priority 1 - If isAligned = true:
     *    • Reason: "I have aligned my own route with a Star Route"
     *    • Means: Route geometry matches a star route within acceptable limits
     *    • Source: Set during Save & Exit via backend API /bpr/adopted
     *    
     *    Priority 2 - Else if isFuelEfficient = true:
     *    • Reason: "Vessel's own route is more fuel efficient"
     *    • Means: Route uses less fuel than ALL available star routes
     *    • Source: Calculated in approveBtnClicked() by fuel comparison
     * 
     * WHY THIS MATTERS:
     * • Justifies to shore operations why vessel chose own route over star route
     * • Provides evidence: either geometric alignment OR fuel savings
     * • Only sent until one day after ETD (never after ETA), also for plans sent shortly after departure
     * • Not sent when WR advise exists (WR takes precedence)
     * 
     * DATA SOURCES:
     * • this._isAligned: From route.bprAlignment.isAligned (Save & Exit flow)
     * • this._isFuelEfficient: From fuel comparison (approveBtnClicked)
     */
    async approveAndSend() {
        if (!this.plan) {
            throw Error('Cannot approve plan due to missing plan')
        }

        this._showConfirmation = false
        this._approving = true

        const offline = isVesselOnCloud && !isOnline();

        // ==========================================================================
        // CAPTURE PHASE (runs BEFORE approvePlan so the approved plan sent to shore
        // carries the notifications in plan.metadata and no join against the
        // notifications service is needed).
        //
        // This must NEVER block the approve flow: any capture failure is recorded
        // as a captureError on the metadata object instead of throwing, and the
        // notifications themselves are still posted after approval as before.
        // ==========================================================================
        const selectedRouteIndex = this.plan!.routes.findIndex(route => route.routeId === this.plan!.currentSelectedRouteId);
        const selectedRoute = this.plan!.routes[selectedRouteIndex];
        const KmToNM = 1 / 1.852;
        const routeDistance = selectedRoute?.route_info?.totals?.non_eca?.distance
            ? selectedRoute.route_info.totals.non_eca.distance * KmToNM
            : -1;
        const routeFuelConsumption = selectedRoute?.route_info?.totals?.non_eca?.fuel ?? -1;

        // Build the 'Route' notification payload (non-throwing; sets captureError
        // internally on partial/total failure, and isOffline when offline).
        let routeSelectionData: RouteSelectionNotificationData;
        try {
            routeSelectionData = await VesselNotificationsService.buildRouteNotificationData(
                this.plan!.scheduleId,
                this.plan!.id ?? 0,
                this.plan!.eta,
                this.plan!.etd,
                routeDistance,
                routeFuelConsumption,
                this._isAligned,
                this._isFuelEfficient,
                this.plan!.departureTerminalCode,
                this.plan!.arrivalTerminalCode,
                selectedRoute?.weatherRoutingInfo,
                offline,
                this.plan!.routes,
                this.plan!.currentSelectedRouteId,
            );
        } catch (err) {
            // Defensive: build is designed not to throw, but approval must proceed
            // regardless — record the failure on the metadata object instead.
            console.error('Failed to build route notification data for plan metadata', err);
            routeSelectionData = {
                planId: this.plan!.id ?? 0,
                scheduleId: this.plan!.scheduleId,
                isWrAdviseReceived: false,
                isBPRCalculationCompleted: false,
                etd: typeof this.plan!.etd === 'string' ? new Date(this.plan!.etd).getTime() : this.plan!.etd,
                eta: typeof this.plan!.eta === 'string' ? new Date(this.plan!.eta).getTime() : this.plan!.eta,
                createdAtFrontendUnixUTC: Date.now(),
                routeDistanceNm: routeDistance,
                routeFuelConsumption: routeFuelConsumption,
                isMoreFuelEfficientThanStarRoute: this._isFuelEfficient,
                isAlignedToStarRoute: this._isAligned,
                bprCalculationLog: null,
                bprIDsInScheduleEntry: [],
                bprsOfferedToUser: [],
                // Derived from the plan rather than fetched, so it survives even the
                // defensive path where the whole build threw.
                bprsShownToUser: getStarRoutesInPlan(this.plan!.routes, this.plan!.currentSelectedRouteId),
                isVesselOnCloud: isVesselOnCloud,
                ...(offline ? { isOffline: true } : {}),
                captureError: `failed to build route notification data: ${err instanceof Error ? err.message : String(err)}`,
            };
        }

        // Capture the BprReject (own-route reason) notification, if the user went
        // through the reject popup before confirming.
        let bprRejectData: BprRejectNotificationData | undefined;
        try {
            const popupData = VesselRouteSelectionModel?.getLastBprRejectPopupData();
            if (popupData && this.isWithinReasonWindow()) {
                const ctx = VesselRouteSelectionModel!.getNotificationContext();
                bprRejectData = {
                    reason: popupData.reason,
                    comments: popupData.comment,
                    scheduleId: ctx.scheduleId ?? this.plan!.scheduleId,
                    departureTerminal: ctx.departureTerminalCode ?? this.plan!.departureTerminalCode,
                    arrivalTerminal: ctx.arrivalTerminalCode ?? this.plan!.arrivalTerminalCode,
                    plansId: ctx.planId ?? this.plan!.id ?? 0,
                    eta: typeof ctx.eta === 'string' ? new Date(ctx.eta).getTime() : ctx.eta,
                    etd: typeof ctx.etd === 'string' ? new Date(ctx.etd).getTime() : ctx.etd,
                    bprRankIDs: ctx.bprRankIDs,
                };
            }
        } catch (err) {
            console.error('Failed to capture BprReject notification data for plan metadata', err);
            bprRejectData = {
                scheduleId: this.plan!.scheduleId,
                plansId: this.plan!.id ?? 0,
                captureError: `failed to capture BprReject notification data: ${err instanceof Error ? err.message : String(err)}`,
            };
        }

        let hasWRAdviseForSchedule: boolean | null = null;
        try {
            hasWRAdviseForSchedule = await VoyageWeatherRoutingModel?.hasWRAdviseForSchedule(this.plan!.scheduleId) ?? false;
        } catch (err) {
            console.warn('Could not check WR advise status for Star Route WR RUM event:', err);
        }

        if (!this.isWithinReasonWindow()) {
            bprRejectData = undefined;
        }

        captureStarRouteWrWorkflowEvent("star-route-wr-approval-context", {
            scheduleId: this.plan!.scheduleId,
            planId: this.plan!.id ?? null,
            selectedRouteId: selectedRoute?.routeId,
            selectedRouteCreatedBy: selectedRoute?.routeCreatedBy,
            selectedRouteName: selectedRoute?.name,
            starRoutesAvailable: this.plan!.routes.filter(route => route.routeCreatedBy === 'system').length,
            isAlignedToStarRoute: this._isAligned,
            isMoreFuelEfficientThanStarRoute: this._isFuelEfficient,
            hasWRAdvise: hasWRAdviseForSchedule,
            bprRejectReason: bprRejectData?.reason,
            bprRejectCommentProvided: !!bprRejectData?.comments,
            routeSelectionCaptureError: routeSelectionData?.captureError,
            bprRejectCaptureError: bprRejectData?.captureError,
        });

        const capturedNotifications: CapturedPlanNotifications = {
            routeSelection: routeSelectionData,
            ...(bprRejectData ? { bprReject: bprRejectData } : {}),
        };

        await VesselPlanningModel!.approvePlan(this.plan!.scheduleId, undefined, capturedNotifications)

       if(isVesselOnCloud && !isOnline()) {
        this._handleBgSync();
        faro.api.pushEvent("offline-plan-submission", { planId: `${this.plan.id}`, scheduleId: `${this.plan.scheduleId}`});
        await VesselNotificationsModel?.loadSyncNotifications();
       }

       if(isVesselOnCloud) {
        const userName = AuthService.getSessionUsername()|| '';
        const name = userName.split("@")[0];
        const imo = AuthService.getSessionVesselIMO();
        const isVesselUser = AuthService.extractVesselIMO(userName);
        const payload = isVesselUser ? {imo: `${imo}`} : {username: `${btoa(name)}`}; // base 64 encoding
        faro.api.pushEvent("plan-submission-by", payload);
       }

        // Route notification data is only built and submitted before ETD; offline
        // submissions are rechecked against the same window during background sync.
        const routeNotificationData = routeSelectionData && this.isWithinReasonWindow()
            ? await VesselNotificationsService.submitRouteNotificationData(routeSelectionData)
            : null;

        // Persist BprAdoption on the selected route
        if (routeNotificationData && selectedRoute && this.isWithinReasonWindow()) {
            const popupData = this.isWithinReasonWindow()
                ? VesselRouteSelectionModel?.getLastBprRejectPopupData()
                : undefined;
            const bprAdoption: BprAdoption = {
                ...routeNotificationData,
                popupShown: !!popupData,
                popupSkipReason: popupData ? undefined : this._popupSkipReason,
                reason: popupData?.reason,
                comment: popupData?.comment,
            };
            await VesselPlanningModel!.updateRouteBprAdoption(
                this.plan!.scheduleId,
                selectedRoute.routeId,
                bprAdoption
            );
            VesselRouteSelectionModel?.clearLastBprRejectPopupData();
        }
        if (!this.isWithinReasonWindow()) {
            VesselRouteSelectionModel?.clearLastBprRejectPopupData();
        }

        // Send star route adoption notification if route is either aligned OR more fuel efficient
        // Only send if WR advise does NOT exist for this voyage
        if ( (this._isAligned || this._isFuelEfficient ) && this.isWithinReasonWindow() && this.hasStarRouteForReason(this._offeredStarRoutes)) {
            // The WR model is not set when weather routing is unavailable — treat
            // that as "no WR advise" instead of crashing out of approveAndSend.
            const hasWRAdvise = hasWRAdviseForSchedule ?? (await VoyageWeatherRoutingModel?.hasWRAdviseForSchedule(this.plan!.scheduleId) ?? false);
            if (!hasWRAdvise) {
                createNotificationAdoptingStarRoute(this.plan!, this.plan!.scheduleId, this._isAligned, this._isFuelEfficient)
            } else {
                captureStarRouteWrWorkflowEvent("star-route-adoption-skipped-due-wr", {
                    scheduleId: this.plan!.scheduleId,
                    planId: this.plan!.id ?? null,
                    selectedRouteId: selectedRoute?.routeId,
                    selectedRouteCreatedBy: selectedRoute?.routeCreatedBy,
                    isAligned: this._isAligned,
                    isFuelEfficient: this._isFuelEfficient,
                    hasWRAdvise,
                });
                console.warn('Star Route adoption notification not sent because WR advise exists for this schedule', {
                    scheduleId: this.plan!.scheduleId,
                    isAligned: this._isAligned,
                    isFuelEfficient: this._isFuelEfficient
                });
            }
        }
    }


    private checkIfOwnRouteIsMoreFuelEfficient(plan: VoyagePlan) {
        const isMoreFuelEfficient = this.isMoreFuelEfficientThanStarRoute(plan);
        if (isMoreFuelEfficient) {
            this.toggleFuelEfficientModal(true);
        }
    }

    /**
     * ==================================================================================
     * PRE-DEPARTURE HELPER: Fuel Efficiency Check
     * ==================================================================================
     * 
     * Determines if own route is more fuel efficient than ALL star routes.
     * 
     * LOGIC:
     * • Gets own route's non-ECA fuel consumption
     * • Gets ALL best practice routes (system-created star routes)
     * • Compares own fuel vs EVERY star route's fuel
     * • Returns true ONLY if: ownFuel < starRoute1.fuel AND ownFuel < starRoute2.fuel AND ...
     * 
     * REQUIREMENT FOR TRUE:
     * • Own route must use LESS fuel than EVERY single star route
     * • If own route >= ANY star route's fuel, returns false
     * 
     * WHY THIS MATTERS (PRE-DEPARTURE):
     * • Provides justification for using own route instead of star route
     * • Prevents "exceed limit" popup if true
     * • Results in notification: "Vessel's own route is more fuel efficient"
     * 
     * @param plan - Voyage plan containing routes to compare
     * @returns true if own route is more fuel efficient than ALL star routes
     */
    private isMoreFuelEfficientThanStarRoute(plan: VoyagePlan) {
        const currentSelectedRoute = plan.routes.find(route => route.routeId === plan.currentSelectedRouteId);
        const bestPracticeRoutes = this.plan?.routes.filter(route => route.routeCreatedBy === 'system') || [];

        if (currentSelectedRoute?.routeCreatedBy === 'system') return;
        if (!bestPracticeRoutes || bestPracticeRoutes.length === 0) return;

        const ownRouteFuel = currentSelectedRoute?.route_info?.totals?.non_eca?.fuel;
        if (!ownRouteFuel) return; // Fuel data for your own route is not available


        let isMoreFuelEfficient = true;
        for (const bpr of bestPracticeRoutes) {
            const bprFuel = bpr?.route_info?.totals?.non_eca?.fuel;
            if (bprFuel !== undefined && ownRouteFuel >= bprFuel) {
                isMoreFuelEfficient = false;
                break; // Best practice route is more or equally fuel-efficient
            }
        }

        // Show modal if the user's route is more fuel efficient
        if (isMoreFuelEfficient) {
            return true;
        }
        return false;
    }

    private checkPlanUpdateFailure() {
        this._approving = false;
        this._planUpdateFailure = true;
        this.requestUpdate()
    }
}
